import { filter } from "lodash";
import { DateHelper } from "../../../../../../../../react-core/Helpers/DateHelper";
import { addDays, addYears, isSameDay } from "../../../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { Frequency } from "../../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { PensionArrangementType } from "../../../../../../../../Wrap/Constants/Enums/Domain/PensionArrangementType";
import { SourceType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SourceType";
import { SubAccountType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { DrawdownType } from "../../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { MoneyOrPercentageType } from "../../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { CappedModel } from "../../../../../../../../Wrap/Models/Domains/Drawdown/CappedModel";
import { DrawdownAnnualIncome } from "../../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import { DrawdownIncomeAmendmentInstruction } from "../../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownIncomeAmendmentInstruction";
import { FlexiModel } from "../../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import { PensionProtectionDetails } from "../../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { PensionTransfer } from "../../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import { PensionBase } from "../../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { IJsonDate } from "../../../../../../../../Wrap/System/IJsonDate";
import { MoneyHelper } from "../../../../../../../Shared/Common/MoneyHelper";
import { ProcessType } from "../../../../../../../Shared/IST/Resource/Content";
import {
    nbSippIncomePaymentDetailsLabels,
    TransferOrDrawdown,
} from "../../../../NewBusinessWizard/Resource/Contents";
import { ChargesHelper } from "../../../Helpers/ChargesHelper";
import { processTypeEnum } from "../../../Resource/Contents";
import {
    calculateNetDrawdownValue,
    totalCashValue,
    totalReRegValue,
} from "../../DrawdownDetails/Helper/DrawdownDetailHelper";
import { Money } from "../../../../../../../../Wrap/Core/DataTypes/Money"
import { PensionContribution } from "../../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PaymentMethod } from "../../../../../../../../Wrap/Constants/Enums/Domain/PaymentMethod";
import { InProgressDrawdownIncome } from "../../../../../../../../Wrap/Models/Domains/Drawdown/InProgressDrawdownIncome";
import { PensionLevelDrawdownIncome } from "../../../../../../../../Wrap/Models/Domains/Drawdown/PensionLevelDrawdownIncome";

export const shouldDisplayDifferentInitialIncomePayment = (
    data: DrawdownAnnualIncome,
    drawdownType: DrawdownType,
    pensionTransfer: PensionTransfer[],
    hasCappedArrangement: boolean,
    processType: processTypeEnum,
    regularCrystalDifferentInital: boolean
): boolean => {
    if (drawdownType === DrawdownType.Regular) {return regularCrystalDifferentInital;}
    return (
        data?.TargetSpecificArrangements != true &&
        !hasCappedArrangement &&
        drawdownType !== DrawdownType.Capped &&
        processType !== processTypeEnum.GadReview &&
        !pensionTransfer?.find((transfer) => {
            return transfer.DrawdownType === DrawdownType.Capped;
        })
    );
};

export const isIncomeExhausted = (
    drawdownType: DrawdownType,
    data: DrawdownAnnualIncome,
    pensionBase: PensionBase,
    processType: processTypeEnum,
    isITFC: boolean
): boolean => {
    let paymentsPerYear = 0;
    if (data?.Frequency) {
        switch (data?.Frequency) {
            case Frequency.Monthly:
                paymentsPerYear = 12;
                break;
            case Frequency.Quarterly:
                paymentsPerYear = 4;
                break;
            case Frequency.HalfYearly:
                paymentsPerYear = 2;
                break;
            case Frequency.Yearly:
                paymentsPerYear = 1;
                break;
        }
    }
    if (drawdownType == DrawdownType.Flexible) {
        let maximumIncomePerPayment = 0;
        let incomePerYear = 0;
        if (data?.TargetSpecificArrangements && data?.GrossIncomePerPaymentFromPension?.Amount) {
            // use target algorithm
            incomePerYear = MoneyHelper.roundNumberToDecimalPlaces(
                MoneyHelper.multiplyBy(data?.GrossIncomePerPaymentFromPension?.Amount, paymentsPerYear).Amount,
                2
            );
            maximumIncomePerPayment = calculateMaximumIncomePerPaymentWithTargetAlgorithm(
                processType,
                pensionBase,
                isITFC
            );
        } else if (data?.TargetSpecificArrangements === false && data?.GrossIncomePerPayment?.Amount) {
            // use standard algorithm
            incomePerYear = MoneyHelper.roundNumberToDecimalPlaces(
                MoneyHelper.multiplyBy(data?.GrossIncomePerPayment?.Amount, paymentsPerYear).Amount,
                2
            );
            maximumIncomePerPayment = calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                processType,
                pensionBase,
                isITFC
            );
        }
        return incomePerYear > maximumIncomePerPayment;
    }
    return false;
};

export const getAssetsToSellTotalForIncome = (data: DrawdownModel): number => {
    return data?.Income?.TargetSpecificArrangements
        ? data?.Income?.GrossIncomePerPaymentFromPension?.Amount
        : data?.Income?.GrossIncomePerPayment?.Amount || 0;
};

export const getAssetsToSellTotalForIncomeFromIncome = (data: DrawdownAnnualIncome): number => {
    return data?.TargetSpecificArrangements
        ? data?.GrossIncomePerPaymentFromPension?.Amount
        : data?.GrossIncomePerPayment?.Amount || 0;
};

export const getTotalIncome = (drawdownModel: DrawdownModel, drawdownIncomeAmendmentInstruction: DrawdownIncomeAmendmentInstruction, pensionBase?: any): number => {
    if (drawdownModel?.Income?.TargetSpecificArrangements || drawdownIncomeAmendmentInstruction?.Income?.TargetSpecificArrangements) {
        const drawdownIncome = drawdownModel?.Income?.GrossIncomePerPayment?.Amount || 0;
        const drawdownIncomeAmendmentIncome = getTargetSpecificArrangementsTotalIncome(drawdownIncomeAmendmentInstruction) 
            || getIncomeFromPensionTransfers(pensionBase?.PensionTransfers);
        return drawdownIncome + drawdownIncomeAmendmentIncome;
    }
    else {
        return drawdownModel?.Income?.GrossIncomePerPayment?.Amount
            || drawdownIncomeAmendmentInstruction?.Income?.GrossIncomePerPayment?.Amount
            || pensionBase?.PensionTransfers?.[0]?.IncomePaymentDetails?.GrossIncomePerPayment?.Amount
            || 0
    }
};

const getTargetSpecificArrangementsTotalIncome = (drawdownIncomeAmendmentInstruction: DrawdownIncomeAmendmentInstruction): number => {
    const cappedArrangements = drawdownIncomeAmendmentInstruction?.CappedArrangements;
    const flexiArrangements = drawdownIncomeAmendmentInstruction?.FlexiArrangements;

    let totalIncome = 0;
    if (drawdownIncomeAmendmentInstruction?.Income?.TargetSpecificArrangements) {
        if (cappedArrangements && cappedArrangements.length > 0) {
            totalIncome += cappedArrangements.reduce((sum, item) => sum + (item?.IncomePerPaymentForCurrentPensionYear || 0), 0);
        }
        if (flexiArrangements && flexiArrangements.length > 0) {
            totalIncome += flexiArrangements.reduce((sum, item) => sum + (item?.IncomePerPayment || 0), 0);
        }
    }
    return totalIncome;
}

const getIncomeFromPensionTransfers = (pensionTransfers: any[]): number => {
    let totalIncome = 0;
    
    if (pensionTransfers && pensionTransfers.length > 0) {
        pensionTransfers.forEach(transfer => {
            if (transfer.PensionSource === PensionArrangementType.Crystallised) {
                const incomeDetails = transfer.IncomePaymentDetails;
                if (transfer.DrawdownType === DrawdownType.Capped) {
                    totalIncome += incomeDetails?.GrossIncomePerPayment?.Amount || 0;
                } else if (transfer.DrawdownType === DrawdownType.Flexible) {
                    totalIncome += incomeDetails?.GrossIncomePerPayment?.Amount || 0;
                }
            }
        });
    }
    
    return totalIncome;
}

export const calculateMaximumAvailablePerPayment = (
    processType: processTypeEnum | ProcessType,
    drawdown: DrawdownModel
): number => {
    if (
        processType == processTypeEnum.Quote ||
        processType == ProcessType.Quote ||
        processType == processTypeEnum.NewBusiness ||
        processType == ProcessType.NB ||
        processType == ProcessType.TakePensionBenefits ||
        processType == processTypeEnum.TakePensionBenefits
    ) {
        const returnValue =
            (drawdown?.RegularCrystallisationDetails?.Amount?.Amount || 0) - (drawdown?.TaxFreeLumpSum?.Amount || 0);
        return returnValue < 0 ? 0 : returnValue;
    }

    return 0;
};

export const calculateMaximumIncomePerPaymentWithStandardAlgorithm = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionContribution,
    isITFC: boolean,
    isInitialMaxIncomePerPayment: boolean = false
): number => {
    if (
        processType == processTypeEnum.Quote ||
        processType == ProcessType.Quote ||
        processType == processTypeEnum.NewBusiness ||
        processType == ProcessType.NB 
    ) {
        const crystallisedTransfer = pensionBase?.PensionTransfers?.filter(
            (e) => e?.PensionSource == PensionArrangementType.Crystallised
        );
        if (hasCappedTransfer(crystallisedTransfer || [])) {
            // Scenario 1 - have capped transfer THEN IGNORE all flexi arrangements
            // incomePerPayment = SUM(GAD maximum / frequency)
            const returnValue = maximumIncomePerPaymentForCappedTransfers(
                crystallisedTransfer?.filter((e) => e?.DrawdownType == DrawdownType.Capped),
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency
            );
            // round down to 2 decimals
            return Math.floor(returnValue * 100) / 100;
        } else if (
            pensionBase?.DrawdownModel?.DrawdownType == DrawdownType.Flexible ||
            hasFlexiTransferOnly(crystallisedTransfer)
        ) {

            // Scenario 2 - only have flexi drawdown or flexi transfer
            const flexiTransfer = maximumIncomePerPaymentForFlexiTransfers(
                crystallisedTransfer?.filter((e) => e?.DrawdownType == DrawdownType.Flexible),
                true
            );
            const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
            const diac = calculateDIAC(pensionBase?.DrawdownModel);
            const productCharge = calculateProductChargeForFlexiTransfer(pensionBase, flexiTransfer);

            const income = pensionBase?.DrawdownModel?.Income || pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome;
            const initialIncomePayment = (!isInitialMaxIncomePerPayment && income?.TakeDifferentInitialIncomePayment)
                ? income?.GrossInitialIncomePayment?.Amount
                : 0;

            // (((Flexi Transfers+Net to single drawdown) - Initial adviser charges taken from product cash)-SIPP Product charges)
            const returnValue = flexiTransfer + netToDrawdown - diac - productCharge - initialIncomePayment;
            return returnValue < 0 ? 0 : returnValue;
        }
    } else if (
        processType == ProcessType.GadReview ||
        processType == processTypeEnum.GadReview ||
        processType == processTypeEnum.EditIncomePayments) {
        const income = pensionBase?.DrawdownModel?.Income || pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income;
        const frequency = income?.Frequency || Frequency.Monthly;

        // calculate capped total if have capped arrangement - apply capped formula
        // (SUM (Each GAD Maximum /Frequency))
        let cappedTotal = 0;
        const cappedArrangements = pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements || [];
        if (cappedArrangements && cappedArrangements.length > 0) {
            cappedArrangements.forEach((e: CappedModel) => {
                const gadMaximum = income?.NextPayment < e.NextGadStartDate ?
                        e?.GadMaximum : (e?.NextYearsGadMaximum || e?.GadMaximum);

                const value = (gadMaximum || 0) / frequencyMap[frequency];

                cappedTotal += value;
            });

            return Math.floor(cappedTotal * 100) / 100;
        }

        // Find all flexi existing arrangements
        const flexiArrangements =pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements || [];
        let flexiTotal = 0;
        if (flexiArrangements && flexiArrangements.length > 0) {
            flexiArrangements.forEach((e: FlexiModel) => {
                const amount = calculateFlexiArrangementAmount(e);
                flexiTotal += amount;
            });
        }

        const initialIncomePayment = (!isInitialMaxIncomePerPayment && income?.TakeDifferentInitialIncomePayment)
            ? income?.GrossInitialIncomePayment?.Amount
            : 0;

        const returnValue = flexiTotal - initialIncomePayment;
        
        return returnValue < 0 ? 0 : returnValue;
        
    } else if (
        processType == processTypeEnum.topUpWithITFLS ||
        processType == ProcessType.TopUpWithITFLS ||
        processType == ProcessType.TakePensionBenefits ||
        processType == processTypeEnum.TakePensionBenefits ||
        processType == ProcessType.WhatIf ||
        processType == processTypeEnum.WhatIfTool) {

        const income = pensionBase?.DrawdownModel?.Income || pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income;
        const frequency = income?.Frequency || Frequency.Monthly;

        // calculate capped total if have capped arrangement - apply capped formula
        // (SUM (Each GAD Maximum /Frequency))
        let cappedTotal = 0;
        const cappedArrangements = pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements || [];
        if (cappedArrangements && cappedArrangements.length > 0) {
            cappedArrangements.forEach((e: CappedModel) => {
                const gadMaximum = income?.NextPayment < e.NextGadStartDate ?
                        e?.GadMaximum : (e?.NextYearsGadMaximum || e?.GadMaximum);

                const value = (gadMaximum || 0) / frequencyMap[frequency];

                cappedTotal += value;
            });

            return Math.floor(cappedTotal * 100) / 100;
        }

        // Flexi arrangements & drawdown
        const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
        const diac = calculateDIAC(pensionBase?.DrawdownModel);

        // calculate flexi total if have flexi arrangements
        const flexiArrangements = pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements?.filter(x => x.From !== "Crystallisation") || [];
        let flexiTotal = 0;
        if (flexiArrangements && flexiArrangements.length > 0) {
            flexiArrangements.forEach((e: FlexiModel) => {
                const amount = calculateFlexiArrangementAmount(e);
                flexiTotal += amount;
            });
        }

        const initialIncomePayment = (!isInitialMaxIncomePerPayment && income?.TakeDifferentInitialIncomePayment)
            ? income?.GrossInitialIncomePayment?.Amount 
            : 0;

        const returnValue = netToDrawdown + flexiTotal - diac - initialIncomePayment;
        return returnValue < 0 ? 0 : returnValue;

    } 
    return 0;
};

export const getInitialPaymentRequested = (pensionBase: PensionBase, processType: processTypeEnum) => {
    if (processType === processTypeEnum.EditIncomePayments) {
        return pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.GrossInitialIncomePayment?.Amount;
    }
    
    return (
        pensionBase?.DrawdownModel?.Income?.GrossInitialIncomePayment?.Amount ||
        (pensionBase?.PensionTransfers &&
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                ?.GrossInitialIncomePayment?.Amount)
    );
};

export const calculateTotalIncomePaymentFromPension = (pensionBase: PensionBase) : Money => {
    const income = pensionBase?.DrawdownModel?.Income;

    let totalIncomePerPaymentFromPension = 0;

    if (income && !income.GrossIncomePerPaymentFromPension && income.AnnualAmount?.Amount && income.Frequency) {
        totalIncomePerPaymentFromPension += income.AnnualAmount.Amount.Amount/frequencyMap[income.Frequency || Frequency.Monthly];
        totalIncomePerPaymentFromPension += (pensionBase?.PensionTransfers ?? [])
            .map(transfer => transfer.IncomePaymentDetails?.GrossIncomePerPayment?.Amount).filter(x => x != null)
            .reduce((sum, amount) => sum + amount, 0);

        return income.GrossIncomePerPaymentFromPension = {
            Amount: totalIncomePerPaymentFromPension,
            Currency: income.AnnualAmount.Amount.Currency
        };
    }
    return income?.GrossIncomePerPaymentFromPension;
}

export const getQuoteIncomePercentage = (pensionBase: PensionBase,
    isDrawdownIncome: boolean,
    currentPensionTransfer: PensionTransfer = null): number => {

    const totalValue = calculateMaximumIncomePerPaymentWithTargetAlgorithm(
        ProcessType.Quote,
        pensionBase,
        false
    )

    if (!totalValue || totalValue == 0) {
        return 100;
    }

    if (isDrawdownIncome) {
        const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
        const diac = calculateDIAC(pensionBase?.DrawdownModel);
        return (netToDrawdown - diac) * 100 / totalValue;
    }

    if (currentPensionTransfer?.DrawdownType == DrawdownType.Flexible) {
        const flexiTransfer = maximumIncomePerPaymentForFlexiTransfers(
            [currentPensionTransfer],
            true
        );
        return flexiTransfer * 100 / totalValue;
    }

    if (currentPensionTransfer?.DrawdownType == DrawdownType.Capped) {
        const firstPaymentDate =
            pensionBase?.DrawdownModel?.Income?.NextPayment ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
        const frequency =
            pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
        const paymentEndDate =
            pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
        const cappedTransfer = cappedTransferWithTarget(
            [currentPensionTransfer],
            firstPaymentDate,
            paymentEndDate,
            frequency
        );
        return cappedTransfer * 100 / totalValue;
    }

    return 100;
}

export const calculateMaximumIncomePerPaymentWithTargetAlgorithm = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean,
): number => {
    if (
        processType == processTypeEnum.Quote ||
        processType == ProcessType.Quote ||
        processType == processTypeEnum.NewBusiness ||
        processType == ProcessType.NB 
    ) {
        // Capped transfer
        // SUM(Each Transfer GAD Maximum)-Each income paid to date) / Pension Year Frequency)
        const crystallisedTransfer = pensionBase?.PensionTransfers?.filter(
            (e) => e?.PensionSource == PensionArrangementType.Crystallised
        );
        const firstPaymentDate =
            pensionBase?.DrawdownModel?.Income?.NextPayment ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
        const frequency =
            pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
        const paymentEndDate =
            pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
        const cappedTransfer = cappedTransferWithTarget(
            crystallisedTransfer?.filter((e) => e?.DrawdownType == DrawdownType.Capped),
            firstPaymentDate,
            paymentEndDate,
            frequency
        );
        const flexiTransfer = maximumIncomePerPaymentForFlexiTransfers(
            crystallisedTransfer?.filter((e) => e?.DrawdownType == DrawdownType.Flexible),
            true
        );
        const productCharge = calculateProductChargeForFlexiTransfer(pensionBase, flexiTransfer);
        const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
        const diac = calculateDIAC(pensionBase?.DrawdownModel);

        // (((SUM(Each Transfer GAD Maximum)-Each income paid to date) / Pension Year Frequency) + (Flexi Transfers+Net to single drawdown) - Initial adviser charge taken from product cash for Flexi)-SIPP Product charges)
        const returnValue = cappedTransfer + flexiTransfer + netToDrawdown - diac - productCharge;
        return returnValue < 0 ? 0 : returnValue;
    } else if (processType == processTypeEnum.topUpWithITFLS || processType == ProcessType.TopUpWithITFLS ||
        processType == processTypeEnum.TakePensionBenefits || processType == ProcessType.TakePensionBenefits) {
        // SUM ((((Each existing GA Maximum - Each income paid to date) - each in progress capped income payments)/ Pension Year Frequency) + (((Each value to move to crystallised account - Drawdown initial adviser charge to come from product cash)-SIPP Product charges) + (value of existing flexi access arrangements - any in progress income payments))).
        // Capped arrangements
        const cappedArrangements = pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements || [];
        let cappedArrangementsValue = 0;
        if (cappedArrangements && cappedArrangements.length > 0) {
            cappedArrangements?.forEach((CappedArrangement) => {
                const value = cappedArrangementWithTarget(CappedArrangement, pensionBase?.DrawdownModel?.Income);
                cappedArrangementsValue += value;
            });
        }

        // Flexi arrangements & drawdown
        const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
        const diac = calculateDIAC(pensionBase?.DrawdownModel);

        // Find all flexi existing arrangements
        const flexiArrangements = pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements?.filter(x => x.From !== "Crystallisation") || [];
        let flexiTotal = 0;
        if (flexiArrangements && flexiArrangements.length > 0) {
            flexiArrangements?.forEach((FlexiArrangement) => {
                const amount = calculateFlexiArrangementAmount(FlexiArrangement);
                flexiTotal += amount;
            });
        }

        const flexiArrangementsValue = netToDrawdown - diac + flexiTotal;

        const returnValue = cappedArrangementsValue + flexiArrangementsValue;
        return returnValue < 0 ? 0 : returnValue;
    } else if (processType == processTypeEnum.EditIncomePayments) {
        return calculateFlexiAndCappedIncomeAvailable(pensionBase);
    } else if (
        processType == processTypeEnum.GadReview ||
        processType == ProcessType.GadReview) {

        return calculateTargetIncomeAvailable(pensionBase);
    }
    return 0;
};

export const calculateTargetIncomeAvailable = (
    pensionBase: PensionBase
): number => {
    const {
        PensionArrangementId: arrangementId,
        CappedArrangements: cappedArrangements,
        FlexiArrangements: flexiArrangements,
    } = pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction;

    let total = cappedArrangements?.reduce(
        (acc, item) => 
            acc + (
                item.PensionArrangementId == arrangementId
                    ? item.MaximumIncomePaymentForFuturePensionYears
                    : item.MaximumIncomePaymentForRemainderOfPensionYear
        ),
        0
    );

    total += flexiArrangements?.reduce((acc, item) => acc + item.MaximumIncomePerPayment, 0);

    return total;
}

export const calculateFlexiAndCappedIncomeAvailable = (
    pensionBase: PensionBase
): number => {
    const isMultiple = pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.TargetSpecificArrangements
    let total =  pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.FlexiArrangements?.reduce((acc, item) => item?.MaximumIncomePerPayment + acc, 0 )
    total +=  pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.CappedArrangements?.reduce((acc, item) => item?.MaximumIncomePaymentForRemainderOfPensionYear + acc, 0 )
    const frequency = pensionBase?.DrawdownModel?.Income?.Frequency ||
        pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.Frequency ||
    Frequency.Monthly;

    return (isMultiple ? total : pensionBase?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.MaximumIncomePerPayment?.Amount / frequencyMap[frequency])  || 0
}

export const calculateMaximumInitialIncomePayment = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean,
): number => {
    if (
        processType == processTypeEnum.Quote ||
        processType == ProcessType.Quote ||
        processType == processTypeEnum.NewBusiness ||
        processType == ProcessType.NB ||
        processType == ProcessType.TakePensionBenefits ||
        processType == processTypeEnum.TakePensionBenefits
    ) {
        if (pensionBase?.DrawdownModel?.DrawdownType == DrawdownType.Regular) {
            // Regular drawdown
            // Maximum initial income payment = (Initial Crystallisation amount – Tax free cash for initial crystallisation)
            // TODO - Tax free cash for initial crystallisation need discuss how to handle this field
            return (
                (pensionBase?.DrawdownModel?.RegularCrystallisationDetails?.InitialAmount?.Amount || 0) -
                (pensionBase?.DrawdownModel?.RegularCrystallisationDetails?.InitialAmountPCLS?.Amount || 0)
            );
        } else {
            return calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                processType,
                pensionBase,
                isITFC,
                true
            );
        }
    }
    if (processType === processTypeEnum.topUpWithITFLS || processType === ProcessType.TopUpWithITFLS ||
        processType == processTypeEnum.EditIncomePayments || processType == processTypeEnum.WhatIfTool) {
        const returnValue = calculateMaximumIncomePerPaymentWithStandardAlgorithm(
            processType,
            pensionBase,
            isITFC,
            true
        );
        return Math.max(0, returnValue);
    }

    return 0;
};

export const calculateGrossIncomePerPayment = (income : DrawdownAnnualIncome) : Money => {
    if (income && !income.GrossIncomePerPayment && (income.AnnualAmount?.Amount || income.CurrentYearIncome) && income.Frequency) {
        let amount = 0;
        let currency = CurrencyCode.GBP;

        if (income.AnnualAmount?.Amount?.Amount) {
            amount = income.AnnualAmount.Amount.Amount;
            currency = income.AnnualAmount.Amount.Currency;
        }
        else{
            amount = income.CurrentYearIncome?.Amount ?? 0;
            currency = income.CurrentYearIncome?.Currency ?? CurrencyCode.GBP;
        }

        return income.GrossIncomePerPayment = {
            Amount: amount/frequencyMap[income.Frequency || Frequency.Monthly],
            Currency: currency
        }
    }
    return income?.GrossIncomePerPayment;
};

export const GetNextYeaStartDateBasedOnPensionYearStartDate = (pensionYearStart: IJsonDate): IJsonDate => {
    // Convert IJsonDate (string or object) into a Date
    let date = JsonDateParser.toDateNoOffset(pensionYearStart);

    // Get today's date (without time for consistency)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Keep adding 1 year until the date is in the future
    while (date <= today) {
        date.setFullYear(date.getFullYear() + 1);
    }

    return JsonDateParser.toJsonDate(date);
};



export const calculateGrossIncomePerPaymentForTargetIncomeAlgorithm = (income: PensionLevelDrawdownIncome, payDate: IJsonDate): Money => {
    const nextFlexiIncomes = (income?.FlexiArrangementsIncomeDetail ?? []).filter(x => x.NextPayment == payDate);
    const nextCappedIncomes = (income?.CappedArrangementsIncomeDetail ?? []).filter(x => x.NextPayment == payDate);

    const currency = nextFlexiIncomes?.[0]?.AnnualAmount?.Amount?.Currency || nextCappedIncomes?.[0]?.IncomeRemainingInCurrentYear?.Currency || nextCappedIncomes?.[0]?.AnnualAmount?.Amount?.Currency || CurrencyCode.GBP;
    const frequency = nextFlexiIncomes?.[0]?.Frequency || nextCappedIncomes?.[0]?.Frequency || Frequency.Monthly;

    const flexiTotalIncome = nextFlexiIncomes.reduce((acc, curr) => acc + curr?.AnnualAmount?.Amount?.Amount, 0);
    const flexiPerPayment = flexiTotalIncome / frequencyMap[frequency];

    let cappedPerPayment = 0;
    
    nextCappedIncomes.forEach((item) => {
        const paymentEnd = item.PaymentEnd;
        const nextYearStart = GetNextYeaStartDateBasedOnPensionYearStartDate(item.PensionYearStart);

        if (item.NextPayment < nextYearStart && (item?.IncomeRemainingInCurrentYear?.Amount ?? 0) > 0) {
            const remainingTimes = calculateBalanceFrequency(
                nextYearStart,
                item.NextPayment,
                paymentEnd,
                frequency
            );
            if (remainingTimes > 0) {
                cappedPerPayment += (item?.IncomeRemainingInCurrentYear?.Amount ?? 0) / remainingTimes;
            }
        }

        if (item.NextPayment >= nextYearStart) {
            cappedPerPayment += (item.AnnualAmount?.Amount?.Amount ?? 0) / frequency;
        }
    })

    return {
        Amount: flexiPerPayment + cappedPerPayment,
        Currency: currency
    }
};

export const calculateNextIncomePerPayment = (income : InProgressDrawdownIncome[]) : Money => {
    const nextIncomePaymentAmount = income.reduce((acc, curr) => acc + curr.IncomeAmount.Amount, 0);
    return {Amount: nextIncomePaymentAmount, Currency: income[0].IncomeAmount.Currency};
}

export const calculateGrossIncomePerPaymentFromPension = (income : DrawdownAnnualIncome) : Money => {
    if (income && !income.GrossIncomePerPaymentFromPension && income.AnnualAmount?.Amount && income.Frequency) {
        return income.GrossIncomePerPaymentFromPension = {
            Amount: income.AnnualAmount.Amount.Amount/frequencyMap[income.Frequency || Frequency.Monthly],
            Currency: income.AnnualAmount.Amount.Currency
        }
    }
    return income?.GrossIncomePerPaymentFromPension;
};

export const calculateMaximumGrossOneOffIncomePayment = (processType: processTypeEnum | ProcessType, pensionBase: PensionBase, isITFC: boolean) : number => {
    const isTarget =
    pensionBase?.DrawdownModel?.Income?.TargetSpecificArrangements ||
    pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
        ?.TargetSpecificArrangements;

    return isTarget == false
    ? calculateMaximumGrossOneOffIncomePaymentWithStandardAlgorithm(
            processType,
            pensionBase,
            isITFC
        )
    : calculateMaximumGrossOneOffIncomePaymentWithTargetAlgorithm(
            processType,
            pensionBase,
            isITFC
        )
}

export const calculateMaximumGrossOneOffIncomePaymentWithStandardAlgorithm = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean
): number => {
    if (processType == processTypeEnum.Quote || processType == ProcessType.Quote) {
        const crystallisedTransfer = pensionBase?.PensionTransfers?.filter(
            (e) => e?.PensionSource == PensionArrangementType.Crystallised
        );
        if (hasCappedTransfer(crystallisedTransfer || [])) {
            // (SUM (Each Transfer GAD Maximum - income paid to date in pension year for each arrangement) - (Income requested x frequency))
            const sumedGad = sumGadMaximumForCappedTransfers(
                pensionBase?.PensionTransfers?.filter(
                    (e) =>
                        e?.PensionSource == PensionArrangementType.Crystallised &&
                        e?.DrawdownType == DrawdownType.Capped
                )
            );
            const incomeRequest =
                (pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossIncomePerPayment?.Amount || 0) *
                frequencyMap[
                    pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency ||
                        Frequency.Monthly
                ];

            const subsequentIncomePaymentsFromPension =
                (pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossIncomePerPaymentFromPension?.Amount || 0) *
                frequencyMap[
                    pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency ||
                        Frequency.Monthly
                ];
            const returnValue = sumedGad - incomeRequest - subsequentIncomePaymentsFromPension;
            return returnValue < 0 ? 0 : returnValue;
        } else if (
            pensionBase?.DrawdownModel?.DrawdownType == DrawdownType.Flexible ||
            hasFlexiTransferOnly(crystallisedTransfer)
        ) {
            // Flexi only
            // ((Maximum income per payment - income requested for initial income payment) - (income requested for subsequent income payments x balance frequency))
            const maximumIncomePerPayment = calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                processType,
                pensionBase,
                isITFC
            );
            const initialPaymentRequested =
                pensionBase?.DrawdownModel?.Income?.GrossInitialIncomePayment?.Amount ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossInitialIncomePayment?.Amount;
            const subsequentIncomePayments =
                pensionBase?.DrawdownModel?.Income?.GrossIncomePerPayment?.Amount ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossIncomePerPayment?.Amount;
            const subsequentIncomePaymentsFromPension =
                pensionBase?.DrawdownModel?.Income?.GrossIncomePerPaymentFromPension?.Amount ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                    ?.GrossIncomePerPaymentFromPension?.Amount;
            const frequency =
                pensionBase?.DrawdownModel?.Income?.Frequency ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
            const firstPaymentDate =
                pensionBase?.DrawdownModel?.Income?.NextPayment ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
            const paymentEndDate =
                pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
                pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
            let balanceFrequency = calculateBalanceFrequency(
                JsonDateParser.toJsonDateNoOffset(new Date()),
                firstPaymentDate,
                paymentEndDate,
                frequency
            );
            balanceFrequency = initialPaymentRequested > 0 ? balanceFrequency - 1 : balanceFrequency;
            const returnValue =
                maximumIncomePerPayment -
                (initialPaymentRequested || 0) -
                (subsequentIncomePayments || 0) * balanceFrequency -
                (subsequentIncomePaymentsFromPension || 0) * balanceFrequency;

            return returnValue < 0 ? 0 : returnValue;
        }
    }
    return 0;
};

export const calculateMaximumGrossOneOffIncomePaymentWithTargetAlgorithm = (
    processType: processTypeEnum | ProcessType,
    pensionBase: PensionBase,
    isITFC: boolean
): number => {
    // Maximum income payment
    const maximumIncomePerPayment = calculateMaximumIncomePerPaymentWithTargetAlgorithm(
        processType,
        pensionBase,
        isITFC
    );
    const subsequentIncomePayments =
        pensionBase?.DrawdownModel?.Income?.GrossIncomePerPayment?.Amount ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.GrossIncomePerPayment
            ?.Amount;
    const subsequentIncomePaymentsFromPension =
        pensionBase?.DrawdownModel?.Income?.GrossIncomePerPaymentFromPension?.Amount ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
            ?.GrossIncomePerPaymentFromPension?.Amount;
    const frequency =
        pensionBase?.DrawdownModel?.Income?.Frequency ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
    const firstPaymentDate =
        pensionBase?.DrawdownModel?.Income?.NextPayment ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
    const paymentEndDate =
        pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
        pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
    const balanceFrequency = calculateBalanceFrequency(
        JsonDateParser.toJsonDateNoOffset(new Date()),
        firstPaymentDate,
        paymentEndDate,
        frequency
    );

    const returnValue =
        maximumIncomePerPayment -
        (subsequentIncomePayments || 0) * balanceFrequency -
        (subsequentIncomePaymentsFromPension || 0) * balanceFrequency;
    return returnValue < 0 ? 0 : returnValue;
};

export const calculateMaximumIncomePaymentForRemainderOfYear = (
    processType: processTypeEnum | ProcessType,
    transferOrDrawdown: TransferOrDrawdown,
    pensionBase: PensionBase
): number => {
    if (processType == processTypeEnum.NewBusiness || processType == ProcessType.NB) {
        // ((GAD Maximum - Income Paid to date) / Pension Year Frequency)
        const gadMaximum = transferOrDrawdown?.IncomePaymentDetails?.GadMaximum?.Amount || 0;
        const incomePaidToDate = transferOrDrawdown?.IncomePaymentDetails?.CurrentYearIncome?.Amount || 0;
        const frequency =
            pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
        const firstPaymentDate =
            pensionBase?.DrawdownModel?.Income?.NextPayment ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.NextPayment;
        const paymentEndDate =
            pensionBase?.DrawdownModel?.Income?.PaymentEnd ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.PaymentEnd;
        const pensionYearFrequency = calculateBalanceFrequency(
            GetNextYearGadReviewStartDate(transferOrDrawdown?.IncomePaymentDetails?.LastGadReviewDate),
            firstPaymentDate,
            paymentEndDate,
            frequency
        );
        if (pensionYearFrequency <= 0) {
            return 0;
        }
        const returnValue = (gadMaximum - incomePaidToDate) / pensionYearFrequency;
        return Math.floor(returnValue * 100) / 100;
    }
    return 0;

};

export const GetNextYearGadReviewStartDate = (lastGadReviewDate: IJsonDate): IJsonDate => {
    // Convert IJsonDate (string or object) into a Date
    let date = JsonDateParser.toDateNoOffset(lastGadReviewDate);

    // Get today's date (without time for consistency)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Keep adding 1 year until the date is in the future
    while (date <= today) {
        date.setFullYear(date.getFullYear() + 1);
    }

    return JsonDateParser.toJsonDate(date);
};

export const calculateMaximumIncomePaymentForRemainderOfYearForExistingArrangement = (
    processType: processTypeEnum | ProcessType,
    cappedModel: CappedModel,
    income: DrawdownAnnualIncome
) => {
    if (processType == processTypeEnum.topUpWithITFLS || processType == ProcessType.TopUpWithITFLS ||
        processType == processTypeEnum.TakePensionBenefits || processType == ProcessType.TakePensionBenefits ||
        processType == processTypeEnum.WhatIfTool || processType == ProcessType.WhatIf ||
        processType == processTypeEnum.EditIncomePayments ||
        processType == processTypeEnum.GadReview ) {
        return cappedArrangementWithTarget(cappedModel, income);
    }
    return 0;
};

export const calculateMaximumIncomePaymentForFutureYearForExistingArrangement = (
    processType: processTypeEnum | ProcessType,
    cappedModel: CappedModel,
    income: DrawdownAnnualIncome
) => {
    if (
        processType == processTypeEnum.WhatIfTool ||
        processType == ProcessType.WhatIf ||
        processType == processTypeEnum.GadReview ||
        processType == ProcessType.GadReview ||
        processType == processTypeEnum.topUpWithITFLS ||
        processType == ProcessType.TopUpWithITFLS ||
        processType == processTypeEnum.TakePensionBenefits ||
        processType == ProcessType.TakePensionBenefits ||
        processType == processTypeEnum.EditIncomePayments 
    ) {
        // NextYearsGadMaximum / frequency
        const returnValue = (cappedModel?.NextYearsGadMaximum || cappedModel?.GadMaximum || 0) / frequencyMap[income?.Frequency || Frequency.Monthly];
        return Math.floor(returnValue * 100) / 100;
    }
    return 0;
};

export const calculateMaximumIncomePaymentForFutureYear = (
    processType: processTypeEnum | ProcessType,
    transferOrDrawdown: TransferOrDrawdown,
    pensionBase: PensionBase
): number => {
    if (processType == processTypeEnum.NewBusiness || processType == ProcessType.NB) {
        // (Next Gad Maximum / Frequency)
        // TODO- next gad maximum hardcode for now
        const nextGadMaximum = transferOrDrawdown?.IncomePaymentDetails?.GadMaximum?.Amount || 0;
        const frequency =
            pensionBase?.DrawdownModel?.Income?.Frequency ||
            pensionBase?.PensionTransfers[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome?.Frequency;
        const returnValue = nextGadMaximum / frequencyMap[frequency || Frequency.Monthly];
        // round down to 2 decimals
        return Math.floor(returnValue * 100) / 100;
    }
    return 0;
};

export const calculateMaximumIncomePaymentForFlexiTarget = (
    processType: processTypeEnum | ProcessType,
    transferOrDrawdown: TransferOrDrawdown,
    pensionBase: PensionBase,
    isITFC: boolean
): number => {
    if (processType == processTypeEnum.NewBusiness || processType == ProcessType.NB) {
        // (Amount for Flexi Access Drawdown - Initial adviser charges taken from product cash)-SIPP Product charges)
        if (transferOrDrawdown?.ProviderDetails?.Name == nbSippIncomePaymentDetailsLabels.crystallisation) {
            // From drawdown
            // Amount for Flexi Access Drawdown = Net to Drawdown
            // IAC = DIAC
            const amountForFlexiAccessDrawdown = calculateNetDrawdownValue(
                pensionBase?.DrawdownModel,
                pensionBase?.PensionProtection
            );
            const drawdownInitialAdviserCharge = calculateDIAC(pensionBase?.DrawdownModel);
            const returnValue = amountForFlexiAccessDrawdown - drawdownInitialAdviserCharge;
            return returnValue < 0 ? 0 : returnValue;
        } else {
            // From transfer
            // Amount for Flexi Access Drawdown = transfer value
            // IAC = Proportion of IAC
            const amountForFlexiAccessDrawdown = maximumIncomePerPaymentForFlexiTransfers([transferOrDrawdown], true);
            const IAC = calculateProportionIAC(
                maximumIncomePerPaymentForFlexiTransfers([transferOrDrawdown], false) || 0,
                pensionBase
            );
            const returnValue = amountForFlexiAccessDrawdown - IAC;
            return returnValue < 0 ? 0 : returnValue;
        }
    } else if (processType == processTypeEnum.topUpWithITFLS || processType == ProcessType.TopUpWithITFLS) {
        const netToDrawdown = calculateNetDrawdownValue(pensionBase?.DrawdownModel, pensionBase?.PensionProtection);
        const diac = calculateDIAC(pensionBase?.DrawdownModel);
        return netToDrawdown - diac
    }
    return 0;
};

export const calculateMaximumIncomePaymentForFlexiTargetForExistingArrangement = (
    processType: processTypeEnum | ProcessType,
    flexiModel: FlexiModel,
    drawdown: DrawdownModel,
    pensionProtection: PensionProtectionDetails
) => {
    if (
        processType == processTypeEnum.WhatIfTool ||
        processType == ProcessType.WhatIf ||
        processType == processTypeEnum.topUpWithITFLS ||
        processType == ProcessType.TopUpWithITFLS ||
        processType == processTypeEnum.TakePensionBenefits ||
        processType == ProcessType.TakePensionBenefits ||
        processType == processTypeEnum.EditIncomePayments||
        processType == processTypeEnum.GadReview
    ) {
        const amountForFlexiAccessDrawdown = calculateFlexiArrangementAmount(flexiModel);

        if (flexiModel?.From == "Crystallisation") {
            // From drawdown
            // Amount for Flexi Access Drawdown = Net to Drawdown
            // IAC = DIAC
            const IAC = calculateDIAC(drawdown);
            const returnValue = amountForFlexiAccessDrawdown - IAC;
            return returnValue < 0 ? 0 : returnValue;
        } else {
            // From existing arrangement
            // Amount for Flexi Access Drawdown = existing amount - current income
            return amountForFlexiAccessDrawdown < 0 ? 0 : amountForFlexiAccessDrawdown;
        }
    }
    // TODO: handle other process types
};

// used for calculating gross one-off income payment
const sumGadMaximumForCappedTransfers = (cappedTransfers: PensionTransfer[]) => {
    let total = 0;
    cappedTransfers.forEach((e) => {
        const value =
            (e?.IncomePaymentDetails?.GadMaximum?.Amount || 0) -
            (e?.IncomePaymentDetails?.CurrentYearIncome?.Amount || 0);
        total += value;
    });
    return total;
};

const cappedTransferWithTarget = (
    transfers: PensionTransfer[],
    firstPaymentDate: IJsonDate,
    paymentEndDate: IJsonDate,
    frequency: Frequency
) => {
    let total = 0;
    transfers = transfers || [];
    firstPaymentDate = firstPaymentDate || JsonDateParser.toJsonDateNoOffset(new Date());
    frequency = frequency || Frequency.Monthly;
    transfers.forEach((e) => {
        const balanceFrequency = calculateBalanceFrequency(
            e?.IncomePaymentDetails?.LastGadReviewDate,
            firstPaymentDate,
            paymentEndDate,
            frequency
        );
        if (balanceFrequency > 0) {
            let value =
                ((e?.IncomePaymentDetails?.GadMaximum?.Amount || 0) -
                    (e?.IncomePaymentDetails?.CurrentYearIncome?.Amount || 0)) /
                balanceFrequency;

            // round down to 2 decimals
            value = Math.floor(value * 100) / 100;
            total += value;
        }
    });
    return total;
};

const cappedArrangementWithTarget = (cappedModel: CappedModel, income: DrawdownAnnualIncome): number => {
    // SUM ((((Each existing GAD Maximum - Each income paid to date) - each in progress capped income payments)/ Pension Year Frequency)
    const gadMaximum = cappedModel?.GadMaximum || 0;
    const incomePaidToDate = cappedModel?.IncomeTakenToDate || 0;
    const frequencyBalance = calculateBalanceFrequency(
        cappedModel?.NextGadStartDate,
        income?.NextPayment,
        income?.PaymentEnd,
        income?.Frequency
    );

    if (frequencyBalance <= 0) {
        return 0;
    }
    const returnValue = (gadMaximum - incomePaidToDate) / frequencyBalance;
    if (returnValue < 0) {
        return 0;
    } else {
        return Math.floor(returnValue * 100) / 100;
    }
};

const maximumIncomePerPaymentForCappedTransfers = (
    cappedTransfers: PensionTransfer[],
    frequency: Frequency
): number => {
    let total = 0;
    cappedTransfers = cappedTransfers || [];
    // Maximum Income Per Payment = (SUM (Each Transfer GAD Maximum/Frequency)
    cappedTransfers.forEach((e) => {
        const value = (e?.IncomePaymentDetails?.GadMaximum?.Amount || 0) / frequencyMap[frequency || Frequency.Monthly];
        total += value;
    });
    return total;
};

export const maximumIncomePerPaymentForFlexiTransfers = (
    flexiTransfers: PensionTransfer[],
    needResidualCash = false
): number => {
    // Maximum Income Per Payment = (SUM(Each Transfer Cash Value))
    let total = 0;
    flexiTransfers = flexiTransfers || [];
    flexiTransfers.forEach((e) => {
        total += e?.CurrentValue?.Amount || 0;
        if (e?.Instruments != null && e?.Instruments?.length > 0) {
            // calculate re-reg value
            e?.Instruments?.forEach((el) => {
                total += (el?.Units || 0) * (el?.Investment?.UnitPrice?.Amount || 0);
            });
            total += needResidualCash ? e?.ReRegisterValue?.Amount || 0 : 0;
        }
    });
    return total;
};

/**
 *
 * @param data PensionBase model
 * @param isITFC if true - will inclue pre-retire transfer re-reg value
 * @param includePostFlexi if true - will include post-retire transfer value (both cash * re-reg)
 * @returns
 */
const calculateIAC = (data: PensionBase, isITFC: boolean, includePostFlexi = true) => {
    // single payment + pre-retirement cash transfer
    let preTotalCash = totalCashValue(data);
    // Not itfc - add pre-retirement re-reg transfer
    preTotalCash += isITFC ? 0 : totalReRegValue(data);
    // add post-retirement flexi transfer (both cash & rereg)
    preTotalCash += includePostFlexi
        ? maximumIncomePerPaymentForFlexiTransfers(
              data?.PensionTransfers?.filter(
                  (e) =>
                      e?.PensionSource == PensionArrangementType.Crystallised &&
                      e?.DrawdownType == DrawdownType.Flexible
              )
          )
        : 0;
    const totalContribution = ChargesHelper.getTotalForIACEstimation(data, SubAccountType.Npr);
    return data?.InitialAdviserCharge?.SourceType == SourceType.WrapCash
        ? 0
        : data?.InitialAdviserCharge?.Type == MoneyOrPercentageType.Money
        ? (preTotalCash * (data?.InitialAdviserCharge?.Money?.Amount || 0)) / totalContribution
        : ((data?.InitialAdviserCharge?.Percentage || 0) * preTotalCash) / 100;
};

/**
 *
 * @param transferAmount current transfer amount
 * @param pensionBase pensionBase model
 * @returns Return de proportion iac value again the total transfer value (include pre/post retire transfer - both cash & re-reg)
 */
const calculateProportionIAC = (transferAmount: number, pensionBase: PensionBase) => {
    const iacType = pensionBase?.TransferInitialAdviceCharge?.Type;
    if (iacType == MoneyOrPercentageType.Money) {
        let totalTransfer = 0;
        pensionBase?.PensionTransfers?.forEach((e) => {
            totalTransfer += e?.CurrentValue?.Amount || 0;
        });
        return (transferAmount / totalTransfer) * (pensionBase?.TransferInitialAdviceCharge?.Money?.Amount || 0);
    } else {
        return (transferAmount * pensionBase?.TransferInitialAdviceCharge?.Percentage) / 100;
    }
};

/**
 *
 * @param drawdown
 * @returns Return DIAC (cash or percetage)
 */
const calculateDIAC = (drawdown: DrawdownModel) => {
    if (drawdown == null) {
        return 0;
    }
    return drawdown?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.SourceType == SourceType.WrapCash
        ? 0
        : drawdown?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Type == MoneyOrPercentageType.Money
        ? drawdown?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money?.Amount || 0
        : ((drawdown?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Percentage || 0) *
              (drawdown?.CrystallisationDetails?.CrystallisationAmount?.Amount || 0)) /
          100;
};

const calculateProductChargeForFlexiTransfer = (data: PensionBase, flexiTransferAmount: number) => {
    if (data?.InitialAdviserCharge == null || data?.InitialAdviserCharge?.SourceType == SourceType.WrapCash) {
        return 0;
    }

    if (data?.InitialAdviserCharge?.Type == MoneyOrPercentageType.Money) {
        const totalContribution = ChargesHelper.getTotalForIACEstimation(data, SubAccountType.Npr);
        return (data?.InitialAdviserCharge?.Money?.Amount || 0) * flexiTransferAmount / totalContribution;
    }

    if (data?.InitialAdviserCharge?.Type == MoneyOrPercentageType.Percentage) {
        return flexiTransferAmount * (data?.InitialAdviserCharge?.Percentage || 0) / 100;
    }
}

/**
 *
 * @param flexiModel Existing flexi arrangement
 * @returns value of existing flexi access arrangements - any in progress income payments
 */
const calculateFlexiArrangementAmount = (flexiModel: FlexiModel) => {
    // TODO - need to determine how to calculate in progress income payments
    return flexiModel.Amount || 0;
};

const hasCappedTransferOnly = (pensionTransfers: PensionTransfer[]): boolean => {
    pensionTransfers = pensionTransfers || [];
    return !pensionTransfers.some((e) => e.DrawdownType == DrawdownType.Flexible);
};

const hasCappedTransfer = (pensionTransfers: PensionTransfer[]): boolean => {
    pensionTransfers = pensionTransfers || [];
    return pensionTransfers.some((e) => e.DrawdownType == DrawdownType.Capped);
};

const hasFlexiTransferOnly = (pensionTransfers: PensionTransfer[]): boolean => {
    pensionTransfers = pensionTransfers || [];
    return !pensionTransfers.some((e) => e.DrawdownType == DrawdownType.Capped);
};

export const calculateBalanceFrequency = (
    nextYearStartDate: IJsonDate,
    firstPaymentDate: IJsonDate,
    paymentEndDate: IJsonDate,
    frequency: Frequency
): number => {
    const frequencyMap = {
        [Frequency.Monthly]: 1,
        [Frequency.Quarterly]: 3,
        [Frequency.HalfYearly]: 6,
        [Frequency.Yearly]: 12,
    };
    if (nextYearStartDate == null || firstPaymentDate == null || frequencyMap[frequency] == null) {
        return 0;
    }
    const currentGadEndDate = addDays(JsonDateParser.toDate(nextYearStartDate), -1)
    const pensionYearEnd =
        (paymentEndDate == null || JsonDateParser.toDate(paymentEndDate) > currentGadEndDate)
            ? currentGadEndDate
            : JsonDateParser.toDate(paymentEndDate);
    const monthDifference = DateHelper.getMonthDifference(pensionYearEnd, JsonDateParser.toDate(firstPaymentDate) ??JsonDateParser.toRawDate(firstPaymentDate));
    return monthDifference < 0
        ? 0
        : frequency == Frequency.Monthly
        ? monthDifference + 1
        : (monthDifference + 1) % frequencyMap[frequency] == 0
        ? (monthDifference + 1) / frequencyMap[frequency]
        : Math.floor((monthDifference + 1) / frequencyMap[frequency]) + 1;
};

const findPensionYearEnd = (pensionYearStart: Date, pensionYearEnd: Date): Date => {
    const nowDate = new Date();
    if ((nowDate >= pensionYearStart || isSameDay(nowDate, pensionYearStart)) && (nowDate <= pensionYearEnd || isSameDay(nowDate, pensionYearEnd))) {
        return pensionYearEnd;
    } else  {
        // instead of doing endless recursive loop, just add a year to the year end until it's after the year start
        // Which is the last GAD review date. We need to add a year at least.
        do {
            pensionYearEnd = addYears(pensionYearEnd, 1);
        }
        while (nowDate > pensionYearEnd);

        return pensionYearEnd;
    }
};


export const changeDateByFrequency = (startDate: Date, frequency: Frequency, modifier: number): Date => {
    const newDate = new Date(startDate);
  
    switch (frequency) {
      case Frequency.Weekly:
        newDate.setDate(newDate.getDate() + modifier * 7);
        break;
      case Frequency.Fortnightly:
        newDate.setDate(newDate.getDate() + modifier * 14);
        break;
      case Frequency.Monthly:
        newDate.setMonth(newDate.getMonth() + modifier * 1);
        break;
      case Frequency.Quarterly:
        newDate.setMonth(newDate.getMonth() + modifier * 3);
        break;
      case Frequency.Termly:
        newDate.setMonth(newDate.getMonth() + modifier * 4);
        break;
      case Frequency.HalfYearly:
        newDate.setMonth(newDate.getMonth() + modifier * 6);
        break;
      case Frequency.Yearly:
        newDate.setFullYear(newDate.getFullYear() + modifier * 1);
        break;
      default:
        throw new Error(`Invalid frequency: ${frequency}`);
    }
  
    return newDate;
  };
  

const NumberOfFrequenciesBetweenDates = (startDate: IJsonDate, frequency: Frequency, endDate: IJsonDate): number => {
    let startTime = JsonDateParser.toDateNoOffset(startDate);
    let endTime = JsonDateParser.toDateNoOffset(endDate);
    let start = new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());
    let end = new Date(endTime.getFullYear(), endTime.getMonth(), endTime.getDate());

    if (end < start) {
        return 0;
    } 
    else if (end.getDate() === start.getDate() && end.getMonth() === start.getMonth() && end.getFullYear() === start.getFullYear()) {
        return 1;//if startDate = endDate return 1
    } 
    else if (
        end.getDate() === start.getDate() &&
        (end.getMonth() !== start.getMonth() || end.getFullYear() !== start.getFullYear())
    ) {
        //Only for scenarios where day of start date  = day of end date and either month or year is different
        let counter = 0;
        while (start <= end) {
            counter++;
            start = changeDateByFrequency(start, frequency, 1);
        }
        return counter;
    } 
    else {
        let counter = 0;
        while (start < end) {
            counter++;
            start = changeDateByFrequency(start, frequency, 1);
        }
        return counter;
    }
  }
  

export const frequencyMap = {
    [Frequency.Monthly]: 12,
    [Frequency.Quarterly]: 4,
    [Frequency.HalfYearly]: 2,
    [Frequency.Yearly]: 1,
};

export const calculateAnnualIncomeAmount = (grossIncomeAmount: number, frequency: Frequency): Money => {
    const frequencyTerms = frequencyMap[frequency];
    let calculatedAmount = 0;

    if (frequencyTerms > 0)
        calculatedAmount = grossIncomeAmount * frequencyTerms;

    return { Currency: MoneyHelper.getDefaultCurrency(), Amount: calculatedAmount };
};

export const calculateAnnualIncomeAmountWithPaymentEnd = (grossIncomeAmount: number, frequency: Frequency, paymentStartDate: IJsonDate, paymentEndDate: IJsonDate): Money =>{
    if (paymentEndDate == null || paymentStartDate == null)
        return { Currency: MoneyHelper.getDefaultCurrency(), Amount: 0 };

    let paymentNumbers = NumberOfFrequenciesBetweenDates(paymentStartDate, frequency, paymentEndDate);
    let calculatedAmount = 0;

    if (paymentNumbers > 0)
        calculatedAmount = grossIncomeAmount * paymentNumbers;

    return { Currency: MoneyHelper.getDefaultCurrency(), Amount: calculatedAmount };
}


export const calculateAnnualIncomeAmountWithInitialValue = (annualAmount: number, initialAmount: number, frequency: Frequency): Money => {
    const frequencyTerms = frequencyMap[frequency];
    let calculatedAmount = 0;

    if (frequencyTerms > 0) {
        var perPayment = annualAmount / frequencyTerms;
        calculatedAmount = initialAmount + perPayment * (frequencyTerms - 1);
    }

    return { Currency: MoneyHelper.getDefaultCurrency(), Amount: calculatedAmount };
};

export const getCurrentPensionYearStart = (pensionYearStart: IJsonDate): IJsonDate => {
    let pensionStartDate = JsonDateParser.toDateNoOffset(pensionYearStart);
    let startDate = new Date(pensionStartDate.getFullYear(), pensionStartDate.getMonth(),  pensionStartDate.getDate());

    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    while (startDate <= todayDate) {
        startDate.setFullYear(startDate.getFullYear() + 1);
    }
    
    startDate.setFullYear(startDate.getFullYear() - 1);
    return JsonDateParser.toJsonDateNoOffset(startDate);
}

export const getCurrentPensionYearEnd = (pensionYearStart: IJsonDate): IJsonDate => {
    let currentPensionYearEnd = JsonDateParser.toDateNoOffset(getCurrentPensionYearStart(pensionYearStart));
    currentPensionYearEnd.setFullYear(currentPensionYearEnd.getFullYear() + 1);
    currentPensionYearEnd.setDate(currentPensionYearEnd.getDate() - 1);
    
    return JsonDateParser.toJsonDateNoOffset(currentPensionYearEnd);
}

export const getDaysUntilMinimumPaymentDate = (paymentMethod: PaymentMethod, needToSellAssetsForDrawdown: boolean, needToSellAssetsForIncome: boolean, takeDifferentInitialIncomePayment: boolean) => {
    const needToSellAssets = needToSellAssetsForDrawdown || needToSellAssetsForIncome;

    let daysUntilFirstPaymentDate = needToSellAssets ? 15 : 10;

    if (paymentMethod && !needToSellAssets) {
        switch (paymentMethod) {
            case PaymentMethod.Faster:
            case PaymentMethod.Chaps:
                daysUntilFirstPaymentDate = 2;
                break;
            case PaymentMethod.Bacs:
                daysUntilFirstPaymentDate = 5;
                break;
            case PaymentMethod.InternalTransfer:
                daysUntilFirstPaymentDate = 2;
                break;
        }
    }

    if (paymentMethod && !needToSellAssetsForDrawdown && needToSellAssetsForIncome) {
        switch (paymentMethod) {
            case PaymentMethod.Faster:
            case PaymentMethod.Chaps:
                daysUntilFirstPaymentDate = 7;
                break;
            case PaymentMethod.Bacs:
                daysUntilFirstPaymentDate = 10;
                break;
            case PaymentMethod.InternalTransfer:
                daysUntilFirstPaymentDate = 7;
                break;
        }
    }

    if (paymentMethod && needToSellAssetsForDrawdown && !needToSellAssetsForIncome) {
        switch (paymentMethod) {
            case PaymentMethod.Faster:
            case PaymentMethod.Chaps:
                daysUntilFirstPaymentDate = 7;
                break;
            case PaymentMethod.Bacs:
                daysUntilFirstPaymentDate = 10;
                break;
            case PaymentMethod.InternalTransfer:
                daysUntilFirstPaymentDate = 7;
                break;
        }
    }

    if (paymentMethod && takeDifferentInitialIncomePayment) {
        switch (paymentMethod) {
            case PaymentMethod.Faster:
            case PaymentMethod.Chaps:
                daysUntilFirstPaymentDate = 7;
                break;
            case PaymentMethod.Bacs:
                daysUntilFirstPaymentDate = 10;
                break;
            case PaymentMethod.InternalTransfer:
                daysUntilFirstPaymentDate = 7;
                break;
        }
    }

    if (paymentMethod && needToSellAssetsForDrawdown && needToSellAssetsForIncome) {
        switch (paymentMethod) {
            case PaymentMethod.Faster:
            case PaymentMethod.Chaps:
                daysUntilFirstPaymentDate = 12;
                break;
            case PaymentMethod.Bacs:
                daysUntilFirstPaymentDate = 15;
                break;
            case PaymentMethod.InternalTransfer:
                daysUntilFirstPaymentDate = 12;
                break;
        }
    }

    return daysUntilFirstPaymentDate;
}
